<template>
  <rs-side-panel ref="sidePanel" class="provider-cluster-panel">
    <template slot="header">
      <span
        class="assignment-header d-flex align-start justify-space-between pb-3 mb-3"
      >
        <span class="assignment-heading">
          <span class="d-block">TYA İlişkilerini Düzenle</span>
          <span
            v-if="provider.name"
            class="assignment-provider d-block text-muted mt-1"
          >
            {{ provider.name }}
          </span>
        </span>
        <v-btn icon :disabled="isSaving" aria-label="Kapat" @click="hide">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </span>
    </template>
    <template slot="body">
      <v-form ref="form" @submit.prevent="handleFormSubmit">
        <v-switch
          v-model="showInactive"
          label="Pasif TYA'ları göster"
          :disabled="isSaving"
          dense
          hide-details
          class="mt-0 mb-3"
        />
        <v-alert v-if="loadError" type="error" dense>
          TYA listesi yüklenemedi.
          <a href="#" class="white--text" @click.prevent="loadList"
            >Tekrar dene</a
          >
        </v-alert>
        <v-data-table
          class="provider-cluster-assignments"
          :fixed-header="false"
          :height="undefined"
          v-bind="dataTableAttrs"
          :headers="headers"
          :items="list"
          :loading="isLoading"
          :options.sync="options"
          :server-items-length="total"
          :items-per-page.sync="itemsPerPage"
          item-selectable="is_selectable"
          :item-class="itemClass"
          v-model="selectedItems"
          @input="handleSelectionChange"
          @update:items-per-page="handleItemsPerPageUpdated"
          no-data-text="Gösterilecek TYA bulunamadı."
        >
          <!-- eslint-disable-next-line -->
          <template v-slot:body.prepend="{ headers }">
            <rs-table-head-search
              :headers="headers"
              :search.sync="search"
              ref="headSearch"
            />
          </template>

          <!-- eslint-disable-next-line -->
          <template v-slot:item.name="{ item }">
            {{ item.name }}
            <span v-if="!item.is_active" class="text-muted ms-1">(Pasif)</span>
          </template>

          <!-- eslint-disable-next-line -->
          <template v-slot:item.has_bank_integration="{ item }">
            <v-icon v-if="item.has_bank_integration" color="success">
              mdi-check
            </v-icon>
            <v-icon v-else color="warning">mdi-close</v-icon>
          </template>

          <!-- eslint-disable-next-line -->
          <template v-slot:item.is_assigned="{ item }">
            <span
              :class="{ 'font-weight-bold': changes[item.id] !== undefined }"
            >
              {{ assignmentStatus(item) }}
            </span>
          </template>

          <!-- eslint-disable-next-line -->
          <template v-slot:item.data-table-select="{ item, isSelected, select }">
            <v-tooltip bottom :disabled="!item.selection_lock_reason">
              <template v-slot:activator="{ on, attrs }">
                <span
                  class="assignment-selection-control d-inline-flex align-center justify-center"
                  v-bind="attrs"
                  v-on="on"
                  :tabindex="item.selection_lock_reason ? 0 : -1"
                >
                  <v-simple-checkbox
                    v-if="!item.selection_lock_reason"
                    :value="isSelected"
                    :disabled="!item.is_selectable || isLoading || isSaving"
                    :aria-label="`${item.name} eşleştirmesi`"
                    @input="select($event)"
                  />
                  <v-icon
                    v-if="item.selection_lock_reason"
                    small
                    color="secondary"
                    :aria-label="item.selection_lock_reason"
                  >
                    mdi-lock
                  </v-icon>
                </span>
              </template>
              <span>{{ item.selection_lock_reason }}</span>
            </v-tooltip>
          </template>
        </v-data-table>

        <p v-if="hasChanges" class="mt-3 mb-0">
          {{ addClusterIds.length }} TYA eklenecek,
          {{ removeClusterIds.length }} TYA kaldırılacak.
        </p>
        <rs-form-buttons
          :isLoading="isLoading || isSaving"
          :disable-submit="!hasChanges || loadError"
          @cancel="hide()"
          hide-save-close
          @submit="handleFormSubmit"
        />
      </v-form>
      <rs-confirm
        ref="confirmSave"
        :loading="isSaving"
        @confirmed="saveAssignments"
        @cancelled="$refs.confirmSave.hide()"
      />
    </template>
  </rs-side-panel>
</template>

<script>
import { debounce } from "@/core/plugins/debounce";
import { hasDataTable } from "@/view/mixins";

export default {
  props: {
    provider: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  mixins: [hasDataTable],
  computed: {
    addClusterIds() {
      return Object.keys(this.changes)
        .filter((id) => this.changes[id])
        .map(Number);
    },
    removeClusterIds() {
      return Object.keys(this.changes)
        .filter((id) => !this.changes[id])
        .map(Number);
    },
    hasChanges() {
      return Object.keys(this.changes).length > 0;
    },
  },
  data() {
    return {
      providerId: null,
      isOpen: false,
      isSaving: false,
      showInactive: false,
      loadError: false,
      requestId: 0,
      changes: {},
      headers: [
        {
          value: "data-table-select",
          align: "center",
          width: 48,
        },
        {
          text: "İsim",
          value: "name",
          searchable: "text",
          sortable: true,
        },
        {
          text: this.$t("labels.status"),
          value: "is_assigned",
          searchable: false,
          sortable: false,
          align: "center",
        },
        {
          text: "Banka Entegrasyonu",
          value: "has_bank_integration",
          searchable: "yesNo",
          sortable: true,
          align: "center",
        },
        {
          text: this.$t("labels.authorized_person"),
          value: "authorized_person",
          searchable: "text",
          sortable: true,
        },
        {
          text: this.$t("labels.house_count"),
          value: "house_count",
          searchable: false,
          sortable: true,
          align: "right",
        },
      ],
      search: {},
      total: null,
      options: {
        sortBy: ["name"],
        sortDesc: [false],
      },
      list: [],
    };
  },
  watch: {
    options: {
      handler() {
        this.loadList();
      },
      deep: true,
    },
    search: {
      handler: debounce(function () {
        this.options.page = 1;
        this.loadList();
      }),
      deep: true,
    },
    showInactive() {
      this.options.page = 1;
      this.loadList();
    },
  },
  methods: {
    loadList() {
      if (!this.isOpen || this.isSaving) {
        return;
      }

      if (this.$refs.confirmSave) this.$refs.confirmSave.hide();
      this.isLoading = true;
      this.loadError = false;
      const requestId = ++this.requestId;
      this.list = [];
      this.selectedItems = [];

      const params = Object.assign(
        this.buildSearchParams(this.search, this.options)
      );

      params.include_inactive = this.showInactive;
      params.per_page = this.itemsPerPage;

      return this.$api
        .query(`company-providers/${this.providerId}/clusters`, { params })
        .then((response) => {
          if (requestId !== this.requestId) return;
          if (response.data && response.data.data) {
            this.list = response.data.data;
            this.list.forEach((item) => {
              if (
                !item.is_selectable ||
                this.changes[item.id] === item.is_assigned
              ) {
                this.$delete(this.changes, item.id);
              }
            });
            this.selectedItems = this.list.filter(this.isSelected);
          }

          if (response.data && response.data.meta) {
            this.total = response.data.meta.total;
            this.options.page = response.data.meta.current_page;
            this.options.itemsPerPage = this.itemsPerPage;
          }
        })
        .catch((error) => {
          if (requestId !== this.requestId) return;
          this.loadError = true;
          this.handleError(error);
        })
        .finally(() => {
          if (requestId === this.requestId) this.isLoading = false;
        });
    },
    isSelected(item) {
      return Object.prototype.hasOwnProperty.call(this.changes, item.id)
        ? this.changes[item.id]
        : item.is_assigned;
    },
    itemClass(item) {
      return this.isSelected(item) !== item.is_assigned
        ? "assignment-pending"
        : "";
    },
    assignmentStatus(item) {
      if (this.changes[item.id] !== undefined) {
        return this.changes[item.id] ? "Eklenecek" : "Kaldırılacak";
      }
      return item.is_assigned ? "Ekli" : "Ekli değil";
    },
    handleSelectionChange(items) {
      if (this.isLoading || this.isSaving) {
        this.selectedItems = this.list.filter(this.isSelected);
        return;
      }
      const ids = new Set(items.map((item) => item.id));
      this.list.forEach((item) => {
        if (!item.is_selectable) return;
        const selected = ids.has(item.id);
        if (selected === item.is_assigned) {
          this.$delete(this.changes, item.id);
        } else {
          this.$set(this.changes, item.id, selected);
        }
      });
      this.selectedItems = this.list.filter(this.isSelected);
    },
    show(params) {
      this.providerId = params.provider_id;
      this.changes = {};
      this.selectedItems = [];
      this.showInactive = false;
      this.search = {};
      this.options.page = 1;
      this.isOpen = true;
      this.$refs.sidePanel.show();
      this.loadList();
    },
    hide() {
      this.isOpen = false;
      this.requestId++;
      this.isLoading = false;
      this.$refs.sidePanel.hide();
    },
    handleFormSubmit() {
      if (
        this.isLoading ||
        this.isSaving ||
        this.loadError ||
        !this.hasChanges
      ) {
        return false;
      }

      this.$refs.confirmSave.show(
        `${this.addClusterIds.length} TYA'ya eklenecek, ${this.removeClusterIds.length} TYA'dan kaldırılacak. Değişiklikleri kaydetmek istiyor musunuz?`
      );
    },
    saveAssignments() {
      if (
        this.isLoading ||
        this.isSaving ||
        this.loadError ||
        !this.hasChanges
      ) {
        return false;
      }

      this.$refs.confirmSave.hide();
      const formData = {
        add_cluster_ids: this.addClusterIds,
        remove_cluster_ids: this.removeClusterIds,
      };

      this.isSaving = true;

      return this.$api
        .put(`company-providers/${this.providerId}/clusters`, formData)
        .then((response) => {
          const result = response.data.data;
          this.$toast.success(
            `${result.added_count} TYA'ya eklendi, ${result.removed_count} TYA'dan kaldırıldı.`
          );
          this.$emit("saved", response.data.data);
          this.$emit("created", response.data.data);
          this.$nextTick(() => this.hide());
        })
        .catch((error) => {
          this.handleError(error);
        })
        .finally(() => {
          this.isSaving = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.provider-cluster-panel ::v-deep .side-panel-header > h3 {
  margin: 0;
}

.assignment-header {
  gap: 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  font-size: 20px;
  font-weight: 600;
  line-height: 1.4;
}

.assignment-heading {
  min-width: 0;
  overflow-wrap: anywhere;
}

.assignment-provider {
  font-size: 14px;
  font-weight: 400;
}

.assignment-selection-control {
  width: 24px;
  height: 24px;
  vertical-align: middle;
}

.v-data-table.provider-cluster-assignments ::v-deep {
  > .v-data-table__wrapper > table > thead > tr > th:first-child,
  > .v-data-table__wrapper > table > tbody > tr > td:first-child {
    padding: 0 10px !important;
    vertical-align: middle;
  }

  .v-simple-checkbox .v-input--selection-controls__input {
    margin: 0;
  }

  tbody tr:not(:hover) {
    background-color: transparent !important;
  }

  tbody tr.assignment-pending > td:first-child {
    box-shadow: inset 3px 0 0 var(--v-primary-base, #1976d2);
  }
}
</style>
